/**
 * Wrap - A component that wraps its children with a div element.
 *
 * @author Koh Tong Seng <tongsengkoh@ifca.com.my>
 * @since 2024-02-05
 *
 * @param {Object} props - The component props
 * @param {React.ReactNode} [props.children] - The children to render
 * @param {string} [props.className] - The CSS class name to apply
 * @param {AlignItemsEnum} [props.crossAxisAlignment] - The cross axis alignment
 * @param {'row' | 'column'} props.direction - The flexbox direction
 * @param {boolean} [props.fullWidth] - Whether to take up the full width
 * @param {string} [props.gap] - The gap between children
 * @param {string} [props.height] - The height of the component
 * @param {JustifyContentEnum} [props.mainAxisAlignment] - The main axis alignment
 * @param {OverflowEnum} [props.overflow] - The overflow strategy
 *
 * @returns {JSX.Element} The rendered component
 */

import * as React from 'react'
import { AlignItemsEnum, JustifyContentEnum, OverflowEnum } from './LayoutXenum'

type props = {
  children?: React.ReactNode
  colGap?: string
  crossAxisAlignment?: AlignItemsEnum
  direction?: 'row' | 'column'
  fullWidth?: boolean
  height?: string
  mainAxisAlignment?: JustifyContentEnum
  overflow?: OverflowEnum
  rowGap?: string
}

export const Wrap = React.memo<props>(
  ({
    children,
    colGap = '0px',
    crossAxisAlignment,
    direction = 'row',
    fullWidth,
    height,
    mainAxisAlignment,
    overflow = 'visible',
    rowGap = '0px',
  }: props) => {
    return (
      <div
        style={{
          alignItems: crossAxisAlignment,
          display: 'flex',
          flexFlow: `${direction} wrap`,
          gap: `${colGap} ${rowGap}`,
          height,
          justifyContent: mainAxisAlignment,
          overflow: overflow,
          verticalAlign: 'bottom',
          width: fullWidth ? '100%' : 'auto',
          wordBreak: 'break-word',
        }}
      >
        {children}
      </div>
    )
  }
)
